/**
 * Analytics — thin, provider-agnostic wrapper.
 *
 * We use Umami (auto-hébergé, chargé CONDITIONNELLEMENT dans
 * `public/index.html`) for web analytics because it's cookieless,
 * doesn't require a consent banner under EU rules, and auto-tracks SPA
 * route changes. This file exists so we can migrate providers by
 * editing ONE function — not every call site.
 *
 * Rules
 * ─────
 *  - Never send PII in `props`. No email, no case_id, no user_id, no
 *    IP, no name. Enums only (plan tier, unit type, mode, etc.). The
 *    Umami dashboard is not a data lake — it's a business signal.
 *  - Never let a tracking call throw. If the script is blocked (ad-
 *    blocker, offline, CSP glitch), the app must behave normally.
 *  - Never let a tracking call block. It's fire-and-forget. Do NOT
 *    await it, do NOT chain UI updates on its return value.
 *  - Sur les surfaces INTERNES (/admin, /internal, /jurist, portail
 *    avocat connecté), index.html n'injecte pas Umami : `window.umami`
 *    n'existe pas et l'événement part à la poubelle, EN SILENCE. C'est
 *    voulu — on ne mesure pas nos équipes. Aucun log, aucun warning :
 *    une console propre est ce qui permet d'y repérer les vraies
 *    erreurs.
 *
 * Usage
 * ─────
 *   import { track } from '../utils/analytics';
 *   track('checkout_opened', { tier: 'pro' });
 */

/**
 * Fire a custom analytics event. Silently no-ops when the analytics
 * script hasn't loaded (surface interne / blocked / offline / SSR /
 * script pas encore arrivé — il est `defer`, donc un événement tiré
 * très tôt est simplement perdu).
 *
 * @param {string} eventName  short, snake_case slug (e.g. "signup_completed")
 * @param {object} [props]    optional enum-only properties. NO PII.
 */
export function track(eventName, props) {
  if (typeof window === 'undefined') return;
  try {
    const umami = window.umami;
    if (!umami || typeof umami.track !== 'function') return;
    // Umami prend l'objet de données DIRECTEMENT en 2e argument —
    // pas de wrapper `{props: {...}}` comme le faisait Plausible.
    if (props && typeof props === 'object') {
      umami.track(eventName, props);
    } else {
      umami.track(eventName);
    }
  } catch (_) {
    // Ad-blocker / CSP / network — analytics must never crash the app.
  }
}
